/** @jsxImportSource ./simboluri-jsx */
// «PLAN DE ARMARE» — vederea Studioului care arată SETUL DE PLANȘE al
// soluției: colile, pe familii și niveluri (grinzile pe nivel, elementele
// verticale, fundația pe direcții, plăcile pe nivel și față), vizorul colii
// alese și descărcarea ei — sau a setului întreg — ca DXF / DWG.
//
// Coala aleasă stă în URL (`?coala=`), ca un link din pagina unui element să
// deschidă exact coala lui. Desenele vin de la server pe versiunea setului
// (`/coli` → `versiune`), deci o coală deschisă a doua oară nu se mai cere.
// DWG-ul se face din DXF cu un convertor extern: fără el butonul e gri și
// spune de ce; o conversie care pierde entități se descarcă, dar avertizează
// — DXF-ul rămâne fișierul exact.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useT } from "../i18n";
import type { Cheie } from "../i18n";
import MeniuLista from "../nav/MeniuLista";
import {
  getPlanArmareColi, planArmareDesenUrl,
  type ColaPlanArmare, type FamilieCoala, type PlanArmareColi,
} from "./apiStructura";
import { DrawingView } from "./DwgViewer";
import { hashElement, hashMarca } from "./legaturi";
import { esteMarca } from "./marci";
import { descarcaCuRaport, type SetPlanse } from "./PlanArmareElement";

const ORDINE_FAMILII: FamilieCoala[] = ["grinzi", "esv", "fundatie", "placa"];
const NUME_FAMILIE: Record<FamilieCoala, Cheie> = {
  grinzi: "struct.pa.coli_grinzi",
  esv: "struct.pa.coli_esv",
  fundatie: "struct.pa.coli_fundatie",
  placa: "struct.pa.coli_placa",
  cofraj: "struct.pc.coli_niveluri",
  sapatura: "struct.psap.coli",
};

/** Colile, grupate pe familie (în ordinea planșei) și apoi pe nivel, în
 *  ordinea în care le dă serverul. */
export function grupeazaColile(coli: ColaPlanArmare[]):
    { familie: FamilieCoala; niveluri: { nivel: string | null; coli: ColaPlanArmare[] }[] }[] {
  const out: ReturnType<typeof grupeazaColile> = [];
  for (const fam of ORDINE_FAMILII) {
    const ale = coli.filter((c) => c.familie === fam);
    if (!ale.length) continue;
    const niveluri: { nivel: string | null; coli: ColaPlanArmare[] }[] = [];
    for (const c of ale) {
      const ultim = niveluri[niveluri.length - 1];
      if (ultim && ultim.nivel === c.nivel) ultim.coli.push(c);
      else niveluri.push({ nivel: c.nivel, coli: [c] });
    }
    out.push({ familie: fam, niveluri });
  }
  // o familie pe care ordinea n-o cunoaște se arată tot, la coadă
  const restul = coli.filter((c) => !ORDINE_FAMILII.includes(c.familie));
  if (restul.length) out.push({ familie: restul[0].familie, niveluri: [{ nivel: null, coli: restul }] });
  return out;
}

/** Butonul «Descarcă ▾» al unei vederi de planșe, sub lista colilor: meniul
 *  lui alege ce se descarcă — coala aleasă sau setul întreg — și în ce format,
 *  DXF-ul exact sau DWG-ul convertit din el. Din planul de armare (implicit),
 *  de cofraj sau de săpătură (`set`). */
export function Descarcari({ projectId, cloneId, coala, dwgOk, set = "plan_armare" }: {
  projectId: string;
  cloneId: string | null;
  /** coala aleasă în listă; fără ea meniul are doar setul întreg */
  coala: string | null;
  dwgOk: boolean;
  set?: SetPlanse;
}) {
  const t = useT();
  const [meniu, setMeniu] = useState(false);
  const [ocupat, setOcupat] = useState(false);
  const descarca = async (fel: "dxf" | "dwg", tinta: string) => {
    setMeniu(false);
    if (ocupat) return;
    setOcupat(true);
    try {
      await descarcaCuRaport(projectId, cloneId, fel, tinta, set);
    } finally {
      setOcupat(false);
    }
  };
  // grupa unei ținte din meniu: capul ei și cele două formate
  const grupa = (tinta: string) => {
    const tot = tinta === "tot";
    return (
      <>
        <span className="ob-lbl" style={{ padding: "7px 10px 3px" }}>
          {t(tot ? "struct.pa.set_intreg" : "struct.pa.coala_aleasa")}
        </span>
        <button type="button" role="menuitem" className="meniu-rand"
                data-testid={`descarca-dxf-${tot ? "tot" : "coala"}`}
                title={t(tot ? "struct.pa.dxf_tot_titlu" : "struct.pa.dxf_coala_titlu")}
                onClick={() => { void descarca("dxf", tinta); }}>
          {t("struct.pa.fel_dxf")}
        </button>
        <button type="button" role="menuitem" className="meniu-rand" disabled={!dwgOk}
                data-testid={`descarca-dwg-${tot ? "tot" : "coala"}`}
                title={dwgOk
                  ? t(tot ? "struct.pa.dwg_tot_titlu" : "struct.pa.dwg_coala_titlu")
                  : t("struct.pa.dwg_indisponibil")}
                onClick={() => { void descarca("dwg", tinta); }}>
          {t("struct.pa.fel_dwg")}
        </button>
      </>
    );
  };
  return (
    <span style={{ position: "relative", display: "flex" }}>
      <button type="button" className="btn mic" style={{ flex: 1, justifyContent: "center" }}
              disabled={ocupat} aria-haspopup="menu" aria-expanded={meniu}
              data-testid="descarca"
              title={t("struct.pa.descarca_coli_titlu")}
              onClick={() => setMeniu(!meniu)}>
        {ocupat ? t("struct.pa.se_descarca") : t("struct.pa.descarca")} ▾
      </button>
      {meniu && (
        <MeniuLista laInchide={() => setMeniu(false)}>
          {coala && grupa(coala)}
          {grupa("tot")}
        </MeniuLista>
      )}
    </span>
  );
}

export default function PlanArmare({ projectId, cloneId, solutie }: {
  projectId: string;
  /** spațiul din care se citesc planșele (`useStructure().cloneId`) */
  cloneId: string | null;
  /** referința de legătură a rândului privit: din ea se fac linkurile spre
   *  paginile elementelor și ale mărcilor */
  solutie?: string | null;
}) {
  const t = useT();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // `undefined` = cererea e pe drum; `null` = spațiul n-are planșe
  const [set, setSet] = useState<PlanArmareColi | null | undefined>(undefined);

  useEffect(() => {
    if (cloneId == null) {
      setSet(null);
      return;
    }
    let viu = true;
    setSet(undefined);
    getPlanArmareColi(projectId, cloneId)
      .then((c) => { if (viu) setSet(c); })
      .catch(() => { if (viu) setSet(null); });
    return () => { viu = false; };
  }, [projectId, cloneId]);

  const coli = set?.coli ?? [];
  const grupe = useMemo(() => grupeazaColile(coli), [coli]);
  // coala din URL, altfel prima a setului
  const ceruta = params.get("coala");
  const coala = coli.find((c) => c.id === ceruta) ?? coli[0] ?? null;
  const alege = (id: string) => {
    const q = new URLSearchParams(params);
    q.set("coala", id);
    setParams(q, { replace: true });
  };
  const dwgOk = set?.dwg_disponibil !== false;

  const laElement = (id: string | null) => {
    if (!id) return;
    if (esteMarca(id)) navigate(hashMarca(projectId, id, solutie));
    else navigate(hashElement(projectId, id, solutie));
  };

  return (
    <div className="pj-step-page" data-testid="plan-armare">
      <h2>{t("struct.pa.titlu")}</h2>
      {set === undefined ? (
        <div className="sg-muted">{t("comun.se_incarca")}</div>
      ) : !coli.length ? (
        <div className="sg-muted" style={{ marginTop: 10 }}>{t("struct.pa.gol")}</div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
          <aside className="ob-card" style={{ width: 260, flex: "none", padding: "10px 12px" }}
                 data-testid="lista-coli">
            <div style={{ borderBottom: "1px solid var(--line)", paddingBottom: 10,
                          marginBottom: 8 }}>
              <Descarcari projectId={projectId} cloneId={cloneId} coala={coala?.id ?? null}
                          dwgOk={dwgOk} />
            </div>
            {grupe.map((g) => (
              <div key={g.familie} style={{ marginBottom: 10 }}>
                <div className="ob-lbl" style={{ marginBottom: 4 }}>{t(NUME_FAMILIE[g.familie])}</div>
                {g.niveluri.map((n, i) => (
                  <div key={i} style={{ marginBottom: 4 }}>
                    {n.nivel && g.niveluri.length > 1 && (
                      <div className="sg-muted" style={{ fontSize: 11, padding: "2px 6px" }}>{n.nivel}</div>
                    )}
                    {n.coli.map((c) => {
                      const on = coala?.id === c.id;
                      return (
                        <button key={c.id} type="button"
                                className={`ob-coala${on ? " on" : ""}`}
                                aria-pressed={on}
                                title={c.titlu}
                                onClick={() => alege(c.id)}>
                          {c.titlu}
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>
            ))}
          </aside>

          <div style={{ flex: 1, minWidth: 0 }}>
            {coala && (
              <div className="ob-card" style={{ overflow: "hidden" }}>
                <div style={{ height: "calc(100vh - 210px)", minHeight: 460 }}>
                  <DrawingView
                    key={coala.id}
                    src={planArmareDesenUrl(projectId, cloneId, coala.id)}
                    versiune={set?.versiune}
                    title={coala.titlu}
                    embed
                    faraAntet
                    onJump={(id) => laElement(id)}
                    linkEticheta={t("struct.pa.link_element")}
                  />
                </div>
                {coala.elemente.length > 0 && (
                  <div style={{ padding: "10px 14px", borderTop: "1px solid var(--line)",
                                display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {coala.elemente.map((ref) => (
                      <a key={ref} className="ob-chip ob-c-mut" style={{ textDecoration: "none" }}
                         href={hashElement(projectId, ref, solutie)}>
                        {ref}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
